import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { LockKey, IdentificationCard, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const normalize = (v) => {
    const t = v.trim();
    return t.includes("@") ? t.toLowerCase() : t.toUpperCase();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(normalize(identifier), password, rememberMe);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left panel — hero */}
      <div className="hidden lg:flex relative bg-[#082308] text-white p-14 flex-col justify-between overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B4506]/70 via-[#0A2A0A]/85 to-[#000]/90" />
        <div className="relative z-10">
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_hr-employee-portal-1/artifacts/16j2gtd7_GREENRGY%20NEW%20FINAL%20LOGO.webp"
            alt="Greenrgy"
            data-testid="login-brand-logo"
            className="h-14 w-auto object-contain mb-8 drop-shadow-lg"
          />
          <div className="label-caps text-white/70">HR & Finance Portal</div>
          <div className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            RUN YOUR<br />TEAM WITH<br />
            <span className="text-[#8FE39B]">CLARITY.</span>
          </div>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-6 border-t border-white/20 pt-8">
          <div>
            <div className="label-caps text-white/50">01</div>
            <div className="mt-2 text-sm">Register employees with resumes</div>
          </div>
          <div>
            <div className="label-caps text-white/50">02</div>
            <div className="mt-2 text-sm">Daily attendance & reports</div>
          </div>
          <div>
            <div className="label-caps text-white/50">03</div>
            <div className="mt-2 text-sm">Income / expense in ₹</div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-md">
          <div className="label-caps mb-3">Employee Access</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">
            Sign in
          </h1>
          <p className="mt-4 text-sm text-[#6B7280]">
            Enter your Employee ID or Email and password to open the portal.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <label className="label-caps block mb-2">Employee ID / Email</label>
              <div className="relative">
                <IdentificationCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <input
                  data-testid="login-identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  placeholder="ADMIN001 or name@company.com"
                  autoComplete="username"
                  className="w-full border border-[#E5E7EB] pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30 focus:border-[#106109] transition-colors duration-200"
                />
              </div>
            </div>

            <div>
              <label className="label-caps block mb-2">Password</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Your password"
                  autoComplete="current-password"
                  className="w-full border border-[#E5E7EB] pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30 focus:border-[#106109] transition-colors duration-200"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[#374151] cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="login-remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 border border-[#D1D5DB] accent-[#106109] cursor-pointer"
                />
                Remember me for 30 days
              </label>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="forgot-password-link"
                className="text-sm text-[#106109] hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="brand-btn w-full py-3.5 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>

            <DemoLoginChips onFill={(id, pw) => { setIdentifier(id); setPassword(pw); }} />
          </form>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}

function DemoLoginChips({ onFill }) {
  const demos = [
    { id: "ADMIN001", pw: "Admin@12345", label: "Admin (ADMIN001)" },
    { id: "EMP001",   pw: "Emp@12345",   label: "Employee (EMP001)" },
  ];
  return (
    <div className="pt-2 border-t border-[#E5E7EB]" data-testid="demo-login-chips">
      <div className="label-caps text-[10px] text-[#6B7280] mb-2">Demo accounts · for evaluation</div>
      <div className="flex flex-wrap gap-2">
        {demos.map((d) => (
          <button
            key={d.id}
            type="button"
            data-testid={`demo-login-${d.id}`}
            onClick={() => onFill(d.id, d.pw)}
            className="text-xs px-3 py-1.5 border border-[#106109] text-[#106109] hover:bg-[#F0F7EF] font-semibold transition-colors"
          >
            Fill · {d.label}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-[#9CA3AF] mt-2">Click a chip to autofill the form, then press Sign in.</div>
    </div>
  );
}

function ForgotPasswordModal({ onClose }) {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const raw = identifier.trim();
      const value = raw.includes("@") ? raw.toLowerCase() : raw.toUpperCase();
      await api.post("/auth/forgot-password", {
        identifier: value,
        origin: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="forgot-modal">
      <div className="bg-white w-full max-w-md border border-[#E5E7EB] p-8">
        <div className="label-caps mb-2">Password recovery</div>
        <h2 className="font-display font-black text-2xl tracking-tight mb-2">Reset your password</h2>
        <p className="text-sm text-[#6B7280] mb-6">
          Enter your Employee ID or Email. We'll send a secure reset link to the email registered on your account.
        </p>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-caps block mb-2">Employee ID or Email</label>
              <input
                data-testid="forgot-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                placeholder="EMP001 or name@company.com"
                className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} data-testid="forgot-cancel" className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-send"
                className="brand-btn flex-1 py-2.5 text-sm"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-sent">
            <div className="bg-[#ECFDF5] border border-[#A7F3D0] p-4 text-sm text-[#065F46]">
              If an account matches that Employee ID or Email, a password reset link has been emailed to the address on file.
              The link expires in <strong>30 minutes</strong>.
            </div>
            <p className="text-xs text-[#6B7280]">
              Didn't get it? Check your spam folder, or ask your admin to verify the email on your profile.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="forgot-close"
              className="brand-btn w-full py-2.5 text-sm"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
